<template>
    <div class="modal-choice">
        <div class="choice-container" v-if="choices.length > 0">
            <div
                v-for="choice in choices"
                :key="choice.value"
                class="choice-inner-container"
            >
                <div
                    class="choice-wrapper"
                    @click="updateValue(choice)"
                    :class="{
                        selected: selected && selected.value === choice.value,
                        disable: !choice.selectable,
                    }"
                >
                    <div
                        class="choice"
                        :data-cy="`${dataCy}_modalMoveTo_option_${choice.value}`"
                    >
                        <img :src="chooseImage(choice.type)" />
                        <label :for="choice.value"> {{ choice.name }}</label>
                    </div>
                    <div
                        class="check"
                        :data-cy="`${dataCy}_modalMoveTo_close`"
                        v-if="selected && choice.value === selected.value"
                    >
                        <i class="fa-solid fa-check"></i>
                    </div>
                </div>
                <div v-if="choice.children" class="child-choice-container">
                    <RecursiveItem
                        v-for="item in choice.children"
                        :key="item.value"
                        v-model="selected"
                        :item="item"
                        :level="2"
                        :data-cy="`${dataCy}_modalMoveTo`"
                        @select="updateValue"
                    />
                </div>
            </div>
        </div>

        <p v-else>{{ emptyListMessage }}</p>
        <div class="button-wrapper">
            <BasicButton
                @click="accept(selected)"
                :loading="loading"
                :disabled="!selected || loading"
                :data-cy="`${dataCy}_modalMoveTo_button_submit`"
            >
                <p>{{ validateButtonText }}</p>
            </BasicButton>
            <BasicButton
                @click="close"
                color="blue"
                :data-cy="`${dataCy}_modalMoveTo_button_close`"
            >
                {{ cancelButtonText }}
            </BasicButton>
        </div>
    </div>
</template>

<script>
import BasicButton from "@/components/basic/BasicButton.vue"
import RecursiveItem from "@/components/basic/RecursiveItem.vue"
export default {
    name: "MoveTo",
    components: { BasicButton, RecursiveItem },
    props: {
        choices: {
            type: Array,
            required: true,
        },
        validateButton: {
            type: String,
            required: true,
        },
        cancelButton: {
            type: String,
            required: true,
        },
        valid: {
            type: Function,
            required: true,
        },
        refuse: {
            type: Function,
            required: false,
        },
        dataCy: {
            type: String,
            null: null,
        },
    },
    computed: {
        validateButtonText() {
            return this.validateButton
                ? this.validateButton
                : this.$t("button.move")
        },
        cancelButtonText() {
            return this.cancelButton
                ? this.cancelButton
                : this.$t("button.cancel")
        },
        emptyListMessage() {
            switch (this.$route.name) {
                case "ApartmentCreateOrEdit":
                    return this.$t("apartmentsList.moveTo.empty")
                default:
                    return this.$t("global.array.emptyList")
            }
        },
    },

    data() {
        return {
            selected: null,
            loading: false,
        }
    },

    methods: {
        close() {
            if (this.refuse) {
                this.refuse()
            }
            this.closeModal()
        },

        async accept(selected) {
            this.loading = true
            await this.valid(this.selected)
            this.loading = false

            this.closeModal()
        },

        updateValue(value) {
            if (value.selectable) {
                this.selected = value
            }
        },

        chooseImage(value) {
            switch (value) {
                case 0:
                    return require("@/assets/images/buildings/complex-residence-medium.png")
                case 1:
                    return require("@/assets/images/buildings/simplified-residence-medium.png")
                case 2:
                    return require("@/assets/images/buildings/basic-residence-medium.png")
                case "CAGE":
                    return require("@/assets/images/buildings/stairwell-medium.png")
                case "BATIMENT":
                    return require("@/assets/images/buildings/building-medium.png")
                case "APPARTEMENT":
                    return require("@/assets/images/apartement/door.svg")

                default:
                    return null
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables/fontSize.scss";
@import "@/assets/scss/global.scss";
p {
    margin: 0;
}

.modal-choice {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;
    width: 100%;

    .choice-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.4em;
        width: 100%;
        max-height: 300px;
        overflow-y: auto;

        .choice-inner-container {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 0.4em;

            .choice-wrapper {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                gap: 1em;
                padding: 0.2em 0 0.2em 20px;
                border-radius: 10px;
                transition: all ease-in-out 0.1s;

                &:hover {
                    transition: all ease-in-out 0.1s;
                    background-color: $grey-light;
                    cursor: pointer;
                }

                &.selected {
                    transition: all ease-in-out 0.1s;
                    background-color: $grey-dark;
                }

                &.disable {
                    cursor: default;
                    background-color: $white;
                    opacity: 0.4;

                    &:hover {
                        cursor: default !important;
                        background-color: $white;
                    }

                    .choice {
                        label,
                        img {
                            &:hover {
                                cursor: default;
                            }
                        }
                    }
                }

                .choice {
                    display: flex;
                    align-items: center;
                    gap: 1em;

                    img {
                        width: 25px;
                        height: 25px;
                        object-fit: contain;
                    }

                    label {
                        width: 100%;
                        text-wrap: nowrap;
                        text-overflow: ellipsis;
                        overflow: hidden;
                        padding: 0.4em 0;
                        &:hover {
                            cursor: pointer;
                        }

                        font-size: $normal;
                    }
                }

                .check {
                    display: flex;
                    align-items: center;
                    padding-right: 0.7em;
                }
            }
            .child-choice-container {
                display: flex;
                flex-direction: column;
                gap: 0.4em;
                width: 100%;
            }
        }
    }

    .button-wrapper {
        width: 100%;
        display: flex;
        justify-content: center;
        gap: 2em;
    }
}
</style>
